import { useEffect, useState } from "react";
import { Plus, Wrench, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Loader, EmptyState, Card } from "@/components/common";
import { cn } from "@/lib/utils";

const STATUS_LABELS = { aperta: "Aperta", in_trattativa: "In trattativa", assegnata: "Assegnata", conclusa: "Conclusa", annullata: "Annullata" };
const FILTERS = [["", "Tutte"], ["aperta", "Aperte"], ["in_trattativa", "In trattativa"], ["assegnata", "Assegnate"], ["conclusa", "Concluse"], ["annullata", "Annullate"]];
const URGENCY = { normale: "Normale", urgente: "Urgente", emergenza: "Emergenza" };
function dt(v) {
  return new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function ServiceRequestsPage() {
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "", problem: "", location: "", vehicle_plate: "", urgency: "normale" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([apiFetch("/service-requests"), apiFetch("/services")]);
      setRequests(r);
      setCategories(s);
    } catch (e) {
      setError(e.message || "Errore di caricamento.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.category || !form.problem.trim() || !form.location.trim()) {
      setError("Categoria, problema e posizione sono obbligatori.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apiFetch("/service-requests", { method: "POST", body: JSON.stringify({ ...form, problem: form.problem.trim(), location: form.location.trim(), vehicle_plate: form.vehicle_plate.trim() }) });
      setForm({ category: "", problem: "", location: "", vehicle_plate: "", urgency: "normale" });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (req, status) => {
    try {
      const updated = await apiFetch(`/service-requests/${req.id}/status`, { method: "POST", body: JSON.stringify({ status }) });
      setRequests((old) => old.map((it) => (it.id === req.id ? updated : it)));
    } catch (e) {
      setError(e.message || "Riprova.");
    }
  };

  const categoryName = (k) => categories.find((c) => c.category === k)?.name || k;
  const filtered = requests.filter((r) => !filter || r.status === filter);
  const statusColor = (s) => (s === "conclusa" ? "text-[hsl(var(--success))]" : s === "annullata" ? "text-muted-foreground" : s === "aperta" ? "text-primary" : "text-[hsl(var(--warning))]");
  const chip = (active) => `rounded-full px-3.5 py-2 text-xs font-bold ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`;
  const inputCls = "min-h-[48px] w-full rounded-xl border border-border bg-card px-3 text-[15px] outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-card-foreground";

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <PageTitle>Richiedi un servizio</PageTitle>
          <PageSubtitle>Descrivi il problema: la tua richiesta resta tracciata con il suo stato.</PageSubtitle>
        </div>
        <button data-testid="request-add-button" onClick={() => setShowForm((v) => !v)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          <Plus className={cn("h-6 w-6 transition-transform", showForm && "rotate-45")} />
        </button>
      </div>

      {showForm ? (
        <Card>
          <p className="mb-1 text-lg font-extrabold text-foreground">Nuova richiesta</p>
          <label className={label}>Categoria *</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c.category} data-testid={`request-category-${c.category}`} onClick={() => setForm({ ...form, category: c.category })} className={chip(form.category === c.category)}>
                {c.name}
              </button>
            ))}
          </div>
          <label className={label}>Problema *</label>
          <textarea data-testid="request-field-problem" value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} placeholder="Es. Perdita olio motore, spia freni..." className={cn(inputCls, "min-h-[70px] py-2")} />
          <label className={label}>Posizione *</label>
          <input data-testid="request-field-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Es. A1 Area Servizio Secchia, Modena" className={inputCls} />
          <label className={label}>Targa / mezzo</label>
          <input data-testid="request-field-plate" value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })} placeholder="Es. AB123CD" className={inputCls} />
          <label className={label}>Urgenza</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {Object.entries(URGENCY).map(([k, l]) => (
              <button key={k} data-testid={`request-urgency-${k}`} onClick={() => setForm({ ...form, urgency: k })} className={chip(form.urgency === k)}>{l}</button>
            ))}
          </div>
          {error ? <p data-testid="request-form-error" className="mt-2 text-center text-sm text-destructive">{error}</p> : null}
          <button data-testid="request-save-button" onClick={save} disabled={saving} className="mt-3 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} INVIA RICHIESTA
          </button>
        </Card>
      ) : null}

      <h2 className="text-lg font-extrabold text-foreground">Le mie richieste</h2>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`request-filter-${k || "all"}`} onClick={() => setFilter(k)} className={chip(filter === k)}>{l}</button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Wrench} title="Nessuna richiesta" text="Crea la prima richiesta di assistenza con il pulsante in alto." />
      ) : (
        filtered.map((req) => (
          <Card key={req.id} data-testid={`request-card-${req.id}`} className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-extrabold text-foreground">{categoryName(req.category)}</p>
                <p className="mt-1 text-xs text-muted-foreground">{req.problem}</p>
                <p className="mt-1 text-xs text-muted-foreground">{req.location}{req.vehicle_plate ? ` · ${req.vehicle_plate}` : ""} · {URGENCY[req.urgency] || req.urgency}</p>
                <p className="mt-1 text-xs text-muted-foreground">{dt(req.created_at)}</p>
              </div>
              <span className={cn("rounded-full bg-secondary/60 px-2.5 py-1 text-[11px] font-extrabold", statusColor(req.status))}>
                {STATUS_LABELS[req.status] || req.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-border pt-2.5">
              {req.status === "aperta" ? (
                <>
                  <button data-testid={`request-progress-${req.id}`} onClick={() => setStatus(req, "in_trattativa")} className="rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-primary">In trattativa</button>
                  <button data-testid={`request-cancel-${req.id}`} onClick={() => setStatus(req, "annullata")} className="rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-destructive">Annulla</button>
                </>
              ) : null}
              {req.status === "in_trattativa" || req.status === "assegnata" ? (
                <>
                  {req.status === "in_trattativa" ? <button data-testid={`request-assign-${req.id}`} onClick={() => setStatus(req, "assegnata")} className="rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-primary">Assegnata</button> : null}
                  <button data-testid={`request-close-${req.id}`} onClick={() => setStatus(req, "conclusa")} className="rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-[hsl(var(--success))]">Concludi</button>
                </>
              ) : null}
              {req.status === "conclusa" || req.status === "annullata" ? (
                <button data-testid={`request-reopen-${req.id}`} onClick={() => setStatus(req, "aperta")} className="rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-primary">Riapri</button>
              ) : null}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
